import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ROUTES } from '../../constants/routes';
import { colors } from '../../theme/colors';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReturnToDashboard = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = ROUTES.LOGIN;
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: colors.background,
          fontFamily: "'Inter', -apple-system, blinkmacsystemfont, 'Segoe UI', roboto, sans-serif",
        }}>
          <div style={{
            background: colors.backgroundPaper,
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '480px',
            width: '90%',
            boxShadow: '0 6px 20px -2px rgba(8, 145, 178, 0.22), 0 2px 4px rgba(15, 23, 42, 0.05)',
            textAlign: 'center',
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'rgba(239, 68, 68, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '24px',
            }}>
              !
            </div>
            <h2 style={{
              color: colors.textPrimary,
              fontSize: '1.25rem',
              fontWeight: 600,
              margin: '0 0 8px',
            }}>
              Something went wrong
            </h2>
            <p style={{
              color: colors.textSecondary,
              fontSize: '0.875rem',
              lineHeight: 1.6,
              margin: '0 0 24px',
            }}>
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={this.handleReturnToDashboard}
                style={{
                  padding: '10px 20px',
                  borderRadius: '1rem',
                  border: 'none',
                  background: colors.primary,
                  color: colors.backgroundPaper,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = colors.primaryDark)}
                onMouseOut={(e) => (e.currentTarget.style.background = colors.primary)}
              >
                Return to Dashboard
              </button>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '10px 20px',
                  borderRadius: '1rem',
                  border: `2px solid ${colors.primary}`,
                  background: 'transparent',
                  color: colors.primary,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(20, 184, 166, 0.05)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
